/**
 * SSO (Single Sign-On) Utilities
 * Handles authentication via shared cookies with plz-admin
 * 
 * This implementation follows the same pattern as joyz-helpdesk
 * for seamless SSO across PLZ applications.
 */

import type { User } from '../../types/auth.types';
import {
  getTokensFromCookies,
  hasAuthCookies,
  extractUserFromToken,
  isTokenExpiredFromJwt,
  decodeJwtPayload,
  type CookieTokens,
  type KeycloakTokenPayload,
} from './cookies';

/**
 * Result of authentication initialization
 */
export interface AuthInitResult {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: User;
  expiresAt?: number;
  error?: string;
}

/**
 * Determines user role based on token claims
 * This is a simplified version - adjust based on your actual role structure
 */
function determineUserRole(_crowdFeatures?: Record<string, boolean>): User['role'] {
  // For now, default to 'admin' for authenticated users
  // You can extend this based on actual role claims from Keycloak
  return 'admin';
}

/**
 * Builds User object from token data
 */
function buildUserFromToken(sessionToken: string): User | null {
  const tokenData = extractUserFromToken(sessionToken);
  
  if (!tokenData) {
    return null;
  }

  const payload = decodeJwtPayload<KeycloakTokenPayload>(sessionToken);
  
  if (!payload) {
    return null;
  }
  
  return {
    id: tokenData.id,
    email: tokenData.email,
    firstName: payload.given_name || payload.preferred_username?.split('@')[0] || 'User',
    lastName: payload.family_name || '',
    role: determineUserRole(payload.crowd_features),
    communityId: tokenData.communityId,
    communityName: payload.crowd_name || `Community ${tokenData.communityId}`,
  };
}

/**
 * Initializes authentication from cookies
 * This is called on app startup to check if user is already authenticated via plz-admin
 */
export async function initializeAuth(): Promise<AuthInitResult> {
  // Check if we have auth cookies
  if (!hasAuthCookies()) {
    return {
      success: false,
      error: 'No authentication cookies found. Please login via Partner Portal.',
    };
  }

  // Get tokens from cookies
  const { sessionToken, refreshToken } = getTokensFromCookies();

  if (!sessionToken) {
    return {
      success: false,
      error: 'Session token not found in cookies.',
    };
  }

  // Check if token is expired
  if (isTokenExpiredFromJwt(sessionToken)) {
    // Token is expired - we'll rely on the refresh mechanism
    // For now, return as unauthenticated - the axios interceptor will handle refresh
    if (!refreshToken) {
      return {
        success: false,
        error: 'Session expired. Please login again via Partner Portal.',
      };
    }
    
    // We have a refresh token - let the app try to refresh
    console.log('Session token expired, will attempt refresh...');
  }

  // Extract user info from token
  const user = buildUserFromToken(sessionToken);
  const tokenData = extractUserFromToken(sessionToken);

  if (!user || !tokenData) {
    return {
      success: false,
      error: 'Failed to parse user information from token.',
    };
  }

  return {
    success: true,
    token: sessionToken,
    refreshToken: refreshToken || undefined,
    user,
    expiresAt: tokenData.expiresAt,
  };
}

/**
 * Checks if user has valid authentication cookies
 */
export function isAuthenticated(): boolean {
  if (!hasAuthCookies()) {
    return false;
  }

  const { sessionToken } = getTokensFromCookies();
  if (!sessionToken) {
    return false;
  }

  // Check if token is not expired
  return !isTokenExpiredFromJwt(sessionToken);
}

/**
 * Gets current tokens from cookies
 * Useful for making authenticated API requests
 */
export function getCurrentTokens(): CookieTokens {
  return getTokensFromCookies();
}

/**
 * Re-exports for convenience
 */
export { hasAuthCookies, getTokensFromCookies } from './cookies';
